import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "../../components/BackLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Users,
  Send,
  Loader2,
  Bell,
  Mail,
  Clock,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachBroadcast({ user }) {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendNotification, setSendNotification] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/coach/dashboard`, {
        withCredentials: true,
      });
      setTeams(response.data.teams || []);
      if (response.data.teams?.length > 0) {
        setSelectedTeam(response.data.teams[0]);
      }
      await fetchBroadcastHistory();
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBroadcastHistory = async () => {
    try {
      const response = await axios.get(`${API}/academy/coach/broadcasts`, {
        withCredentials: true,
      });
      setBroadcastHistory(response.data.broadcasts || []);
    } catch (err) {
      console.error("Error fetching broadcasts:", err);
    }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }

    if (!selectedTeam) {
      toast.error("Please select a team");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/academy/coach/broadcast`,
        {
          team_id: selectedTeam.id,
          subject,
          message,
          send_email: sendEmail,
          send_notification: sendNotification,
        },
        { withCredentials: true }
      );

      toast.success(`Message sent to ${response.data.recipients} parents`);
      setSubject("");
      setMessage("");
      await fetchBroadcastHistory();
    } catch (err) {
      console.error("Error sending broadcast:", err);
      toast.error("Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <BackLink to="/academy/coach-dashboard" label="Back"
            className="text-white hover:bg-white/20 mb-2 -ml-2"
            testId="broadcast-back" />
          <h1 className="text-2xl font-bold">Send Broadcast</h1>
          <p className="text-cyan-200">Send messages to parents of your team players</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Compose Message */}
          <Card className="bg-slate-900/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Compose Message
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Team Selector */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Select Team</label>
                <Select
                  value={selectedTeam?.id}
                  onValueChange={(id) => setSelectedTeam(teams.find((t) => t.id === id))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Select Team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.player_count || 0} players)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Subject *</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Message subject..."
                  className="bg-slate-800 border-slate-700"
                  data-testid="broadcast-subject"
                />
              </div>

              {/* Message */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Message *</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message to parents..."
                  className="bg-slate-800 border-slate-700 min-h-[150px]"
                  data-testid="broadcast-message"
                />
              </div>

              {/* Delivery Options */}
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-white mb-2">Delivery Options</p>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendNotification"
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <label htmlFor="sendNotification" className="text-sm text-slate-300 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-cyan-400" />
                    Send in-app notification
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-400" />
                    Send email notification
                  </label>
                </div>
              </div>

              {/* Preview */}
              {selectedTeam && (
                <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-sm text-cyan-400">
                    <Users className="w-4 h-4 inline mr-1" />
                    This message will be sent to all parents of players in{" "}
                    <span className="font-medium">{selectedTeam.name}</span>
                  </p>
                </div>
              )}

              {/* Send Button */}
              <Button
                onClick={sendBroadcast}
                disabled={submitting || !subject.trim() || !message.trim() || !selectedTeam}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-black"
                data-testid="send-broadcast-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Broadcast
              </Button>
            </CardContent>
          </Card>

          {/* Broadcast History */}
          <Card className="bg-slate-900/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" /> Recent Broadcasts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {broadcastHistory.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No broadcasts sent yet</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {broadcastHistory.map((broadcast) => (
                    <div
                      key={broadcast.id}
                      className="p-4 bg-[#0a0f1a] rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-white">{broadcast.subject}</p>
                          <p className="text-sm text-slate-400">{broadcast.team_name}</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Sent
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-300 line-clamp-2 mb-2">
                        {broadcast.message}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {broadcast.recipients} recipients
                        </span>
                        <span>
                          {new Date(broadcast.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-2">
                          {broadcast.sent_notification && (
                            <Bell className="w-3 h-3 text-cyan-400" title="Notification sent" />
                          )}
                          {broadcast.sent_email && (
                            <Mail className="w-3 h-3 text-blue-400" title="Email sent" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
